import { useCallback, useEffect, useRef, useState } from 'react';
import paper from 'paper';
import {ANGLE_COEFFICIENT, SNAP_TOLERANCE_PX} from '@/constants/editor';

export function useGuideSnapping({ snapToGuides, customGuides = [] }) {
  const snapToGuidesRef = useRef(snapToGuides);
  const customGuidesRef = useRef(customGuides);

  const [activeSnap, setActiveSnap] = useState({ x: false, y: false });

  useEffect(() => {
    snapToGuidesRef.current = snapToGuides;
  }, [snapToGuides]);

  useEffect(() => {
    customGuidesRef.current = customGuides;
  }, [customGuides]);

  const calculateSnapDelta = useCallback((selectedItems, delta) => {
    if (!snapToGuidesRef.current || selectedItems.length === 0) {
      setActiveSnap({ x: false, y: false });
      return delta;
    }

    const guides = customGuidesRef.current;
    const zoom = paper.view.zoom;
    const tolerance = SNAP_TOLERANCE_PX / zoom;
    let adjustedDelta = delta.clone();

    let totalBounds = null;
    selectedItems.forEach((item) => {
      const itemBounds = item.bounds.clone();
      itemBounds.x += delta.x;
      itemBounds.y += delta.y;

      totalBounds = totalBounds ? totalBounds.unite(itemBounds) : itemBounds;
    });

    if (!totalBounds) {
      setActiveSnap({ x: false, y: false });
      return delta;
    }

    const xPointsToSnap = [totalBounds.left, totalBounds.center.x, totalBounds.right];
    const yPointsToSnap = [totalBounds.top, totalBounds.center.y, totalBounds.bottom];

    let snappedX = false;
    let snappedY = false;

    for (const px of xPointsToSnap) {
      const diffX = 0 - px;
      if (Math.abs(diffX) <= tolerance) {
        adjustedDelta.x += diffX;
        snappedX = true;
        break;
      }
    }

    for (const py of yPointsToSnap) {
      const diffY = 0 - py;
      if (Math.abs(diffY) <= tolerance) {
        adjustedDelta.y += diffY;
        snappedY = true;
        break;
      }
    }

    guides.forEach((guide) => {
      const normAngle = ((guide.angle % ANGLE_COEFFICIENT) + ANGLE_COEFFICIENT) % ANGLE_COEFFICIENT;
      const isVertical = Math.abs(normAngle - 90) < 1 || Math.abs(normAngle - 270) < 1;
      const isHorizontal = Math.abs(normAngle) < 1 || Math.abs(normAngle - ANGLE_COEFFICIENT) < 1;

      if (isVertical && !snappedX) {
        const guideX = guide.x;
        for (const px of xPointsToSnap) {
          const diff = guideX - px;
          if (Math.abs(diff) <= tolerance) {
            adjustedDelta.x += diff;
            snappedX = true;
            break;
          }
        }
      } else if (isHorizontal && !snappedY) {
        const guideY = guide.y;
        for (const py of yPointsToSnap) {
          const diff = guideY - py;
          if (Math.abs(diff) <= tolerance) {
            adjustedDelta.y += diff;
            snappedY = true;
            break;
          }
        }
      } else if (!isVertical && !isHorizontal) {

        const rad = (guide.angle * Math.PI) / ANGLE_COEFFICIENT;
        const nx = -Math.sin(rad);
        const ny = Math.cos(rad);
        
        const allPoints = [
          new paper.Point(totalBounds.left, totalBounds.top),
          new paper.Point(totalBounds.right, totalBounds.top),
          new paper.Point(totalBounds.left, totalBounds.bottom),
          new paper.Point(totalBounds.right, totalBounds.bottom),
          totalBounds.center,
          new paper.Point(totalBounds.center.x, totalBounds.top),
          new paper.Point(totalBounds.center.x, totalBounds.bottom),
          new paper.Point(totalBounds.left, totalBounds.center.y),
          new paper.Point(totalBounds.right, totalBounds.center.y)
        ];

        for (const pt of allPoints) {
          const d = nx * (pt.x - guide.x) + ny * (pt.y - guide.y);
          if (Math.abs(d) <= tolerance) {
            adjustedDelta.x -= d * nx;
            adjustedDelta.y -= d * ny;
            break;
          }
        }
      }
    });

    const finalBounds = totalBounds.clone();
    finalBounds.x += adjustedDelta.x - delta.x;
    finalBounds.y += adjustedDelta.y - delta.y;

    const finalXPoints = [finalBounds.left, finalBounds.center.x, finalBounds.right];
    const finalYPoints = [finalBounds.top, finalBounds.center.y, finalBounds.bottom];
    const finalAllPoints = [
      new paper.Point(finalBounds.left, finalBounds.top),
      new paper.Point(finalBounds.right, finalBounds.top),
      new paper.Point(finalBounds.left, finalBounds.bottom),
      new paper.Point(finalBounds.right, finalBounds.bottom),
      finalBounds.center,
      new paper.Point(finalBounds.center.x, finalBounds.top),
      new paper.Point(finalBounds.center.x, finalBounds.bottom),
      new paper.Point(finalBounds.left, finalBounds.center.y),
      new paper.Point(finalBounds.right, finalBounds.center.y)
    ];

    const EPSILON = 0.05;

    const touchesAxisX = finalXPoints.some((px) => Math.abs(px) < EPSILON);
    const touchesAxisY = finalYPoints.some((py) => Math.abs(py) < EPSILON);

    const snappedGuides = {
      x: touchesAxisX,
      y: touchesAxisY,
      customX: [], 
      customY: [],
      customInclined: []
    };

    guides.forEach((guide) => {
      const normAngle = ((guide.angle % ANGLE_COEFFICIENT) + ANGLE_COEFFICIENT) % ANGLE_COEFFICIENT;
      const isVertical = Math.abs(normAngle - 90) < 1 || Math.abs(normAngle - 270) < 1;
      const isHorizontal = Math.abs(normAngle) < 1 || Math.abs(normAngle - ANGLE_COEFFICIENT) < 1;

      if (isVertical) {
        const guideX = guide.x;
        const isTouching = finalXPoints.some((px) => Math.abs(guideX - px) < EPSILON);
        if (isTouching) snappedGuides.customX.push(guide.id);
      } else if (isHorizontal) {
        const guideY = guide.y;
        const isTouching = finalYPoints.some((py) => Math.abs(guideY - py) < EPSILON);
        if (isTouching) snappedGuides.customY.push(guide.id);
      } else {
        const rad = (guide.angle * Math.PI) / ANGLE_COEFFICIENT;
        const nx = -Math.sin(rad);
        const ny = Math.cos(rad);
        const isTouching = finalAllPoints.some(pt => {
          const d = nx * (pt.x - guide.x) + ny * (pt.y - guide.y);
          return Math.abs(d) < EPSILON;
        });
        if (isTouching) snappedGuides.customInclined.push(guide.id);
      }
    });

    setActiveSnap(snappedGuides);

    return adjustedDelta;
  }, []);

  const resetGuidesHighlight = useCallback(() => {
    setActiveSnap({ x: false, y: false, customX: [], customY: [] });
  }, []);

  return { calculateSnapDelta, resetGuidesHighlight, activeSnap };
}